import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import SearchableDropdown from "../components/SearchableDropdown";
import { apiErrorMessage, type ApiError } from "@/lib/api";

export interface RoomFormData {
  name: string;
  type: string;
  capacity: string;
  rate: string;
  status: string;
}

export interface RoomPayload {
  name: string;
  type: string;
  capacity: number;
  rate: number;
  status: "available" | "maintenance";
}

interface RoomFormProps {
  /** Throws on failure; the form then stays open and shows the server's message */
  onSubmit: (data: RoomPayload) => Promise<void>;
  onCancel: () => void;
  initialData?: Partial<RoomFormData>;
}

/**
 * The server accepts available / maintenance (and "occupied" from older data).
 * Whether a room is booked is worked out from Room Bookings, so it isn't set here.
 */
const STATUS_OPTIONS: { value: RoomPayload["status"]; label: string }[] = [
  { value: "available", label: "Available (in service)" },
  { value: "maintenance", label: "Maintenance (out of service)" },
];

export default function RoomForm({ onSubmit, onCancel, initialData }: RoomFormProps) {
  const [formData, setFormData] = useState<RoomFormData>({
    name: initialData?.name || "",
    type: initialData?.type || "",
    capacity: initialData?.capacity || "",
    rate: initialData?.rate || "",
    // Older labels like "booked"/"occupied" aren't a manual setting any more
    status: initialData?.status === "maintenance" ? "maintenance" : "available",
  });

  const [errors, setErrors] = useState<Partial<Record<keyof RoomFormData, string>>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [roomTypes, setRoomTypes] = useState(() => {
    const base = ["Banquet", "Open Air", "Conference", "Accommodation"];
    return initialData?.type && !base.includes(initialData.type) ? [...base, initialData.type] : base;
  });

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof RoomFormData, string>> = {};

    if (!formData.name.trim()) newErrors.name = "Room name is required";
    if (!formData.type) newErrors.type = "Room type is required";
    if (!formData.capacity || parseInt(formData.capacity) < 1) newErrors.capacity = "Enter how many guests the room takes (at least 1)";
    if (formData.rate === "" || parseFloat(formData.rate) < 0) newErrors.rate = "Enter the rate per day (0 or more)";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (!validate()) return;
    setSaving(true);
    try {
      await onSubmit({
        name: formData.name.trim(),
        type: formData.type,
        capacity: parseInt(formData.capacity),
        rate: parseFloat(formData.rate),
        status: formData.status === "maintenance" ? "maintenance" : "available",
      });
    } catch (error) {
      setFormError(apiErrorMessage(error, "Couldn't save the room"));
      const serverErrors = (error as ApiError)?.errors || {};
      setErrors(Object.fromEntries(Object.entries(serverErrors).map(([k, v]) => [k, v[0]])) as Partial<Record<keyof RoomFormData, string>>);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field: keyof RoomFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const inputClass = (field: keyof RoomFormData) => `
    w-full h-10 px-3 rounded-xl border text-sm transition-colors
    ${errors[field]
      ? "border-rose-300 bg-rose-50 focus:border-rose-400 focus:ring-2 focus:ring-rose-100"
      : "border-stone-200 bg-white focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
    }
    outline-none
  `;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Room Name */}
        <div className="sm:col-span-2">
          <label htmlFor="room-name" className="block text-xs font-medium text-stone-500 mb-1.5">Room Name *</label>
          <input
            id="room-name"
            type="text"
            value={formData.name}
            onChange={(e) => handleChange("name", e.target.value)}
            placeholder="e.g., Room 101"
            className={inputClass("name")}
          />
          {errors.name && <p className="text-xs text-rose-500 mt-1">{errors.name}</p>}
        </div>

        {/* Room Type */}
        <div>
          <SearchableDropdown
            label="Room Type *"
            options={roomTypes.map(t => ({ value: t, label: t }))}
            value={formData.type}
            onChange={(value) => handleChange("type", value)}
            placeholder="Select or type room type"
            allowAddNew
            onAddNew={(value) => {
              setRoomTypes(prev => [...prev, value]);
              handleChange("type", value);
            }}
            error={errors.type}
          />
        </div>

        {/* Status */}
        <div>
          <label htmlFor="room-status" className="block text-xs font-medium text-stone-500 mb-1.5">Status</label>
          <select
            id="room-status"
            value={formData.status}
            onChange={(e) => handleChange("status", e.target.value)}
            className={inputClass("status")}
          >
            {STATUS_OPTIONS.map(s => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
          {errors.status ? (
            <p className="text-xs text-rose-500 mt-1">{errors.status}</p>
          ) : (
            <p className="text-xs text-stone-400 mt-1">Bookings come from Room Bookings.</p>
          )}
        </div>

        {/* Capacity */}
        <div>
          <label htmlFor="room-capacity" className="block text-xs font-medium text-stone-500 mb-1.5">Capacity (guests) *</label>
          <input
            id="room-capacity"
            type="number"
            inputMode="numeric"
            min="1"
            value={formData.capacity}
            onChange={(e) => handleChange("capacity", e.target.value)}
            placeholder="e.g., 2"
            className={inputClass("capacity")}
          />
          {errors.capacity && <p className="text-xs text-rose-500 mt-1">{errors.capacity}</p>}
        </div>

        {/* Rate */}
        <div>
          <label htmlFor="room-rate" className="block text-xs font-medium text-stone-500 mb-1.5">Rate per Day (₹) *</label>
          <input
            id="room-rate"
            type="number"
            inputMode="decimal"
            min="0"
            step="0.01"
            value={formData.rate}
            onChange={(e) => handleChange("rate", e.target.value)}
            placeholder="0.00"
            className={inputClass("rate")}
          />
          {errors.rate && <p className="text-xs text-rose-500 mt-1">{errors.rate}</p>}
        </div>
      </div>

      {formError && (
        <div role="alert" className="flex items-start gap-2 rounded-xl bg-red-50 border border-red-200 px-3 py-2.5 text-sm text-red-700">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{formError}</span>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-3 pt-4 border-t border-stone-100">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="flex-1 h-11 px-4 rounded-xl bg-stone-100 text-stone-600 font-semibold text-sm hover:bg-stone-200 transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="flex-1 h-11 px-4 rounded-xl text-white font-semibold text-sm gold-gradient hover:opacity-90 transition-opacity disabled:opacity-60"
        >
          {saving ? "Saving…" : initialData ? "Update Room" : "Add Room"}
        </button>
      </div>
    </form>
  );
}
